<template>
  <div class="fields-route">
    <div class="grid-layout">
      <!-- Left: Lookup -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="search" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Find device</h3>
            <p class="card-subtitle">Lookup by IDI device ID, serial number, or deviceID</p>
          </div>
        </div>

        <div class="form-grid">
          <div class="lookup-toggle">
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="lookupKind === 'idi_device_id' || null"
              @click="lookupKind = 'idi_device_id'"
            >
              IDI device ID
            </button>
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="lookupKind === 'serial_number' || null"
              @click="lookupKind = 'serial_number'"
            >
              Serial number
            </button>
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="lookupKind === 'deviceID' || null"
              @click="lookupKind = 'deviceID'"
              title="Local lookup in your current org"
            >
              deviceID
            </button>
          </div>

          <NumericInput
            type="text"
            :label="lookupLabel"
            :value="lookupValue"
            :placeholder="lookupPlaceholder"
            @update:value="(v: string) => (lookupValue = v)"
          />
        </div>

        <p v-if="lookupError" class="error_message mb-2">{{ lookupError }}</p>

        <div class="form-actions">
          <button
            class="generate-btn"
            type="button"
            :disabled="!lookupValue.trim() || lookupLoading"
            @click="lookup"
          >
            <Loader v-if="lookupLoading" />
            <span v-else>Lookup device</span>
          </button>
          <button class="clear-btn" type="button" :disabled="lookupLoading" @click="reset">
            Clear
          </button>
        </div>
      </div>

      <!-- Right: Match + Fields editor -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="settings" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Update deploy fields</h3>
            <p class="card-subtitle">
              Updates the device row in place. Required before customer can deploy.
            </p>
          </div>
        </div>

        <div v-if="!matchedDevice" class="empty-match">
          <Icon name="info_circle" :size="20" color="var(--txt-secondary-1)" />
          <p>Look up a device on the left to start.</p>
        </div>

        <template v-else>
          <div class="match-panel">
            <div class="match-row">
              <span class="match-label">deviceID</span>
              <span class="match-value mono">{{ matchedDevice.deviceID }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">deviceType</span>
              <span class="match-value">{{ matchedDevice.deviceType || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">organisation</span>
              <span class="match-value mono">{{ matchedDevice.organisationID }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">onboarded</span>
              <span class="match-value">{{ matchedDevice.onboarded ? 'true' : 'false' }}</span>
            </div>
          </div>

          <div class="fields-section">
            <div class="form-grid">
              <NumericInput
                type="text"
                label="Machine name (Tailscale ID)"
                :value="fields.machineName"
                placeholder="e.g. spaceship"
                @update:value="(v: string) => (fields.machineName = v)"
              />
              <NumericInput
                type="text"
                label="Device IP"
                :value="fields.deviceIp"
                placeholder="e.g. 100.64.0.5"
                @update:value="(v: string) => (fields.deviceIp = v)"
              />
              <NumericInput
                type="text"
                label="MQTT Host"
                :value="fields.mqttHost"
                placeholder="e.g. mqtt.example.com"
                @update:value="(v: string) => (fields.mqttHost = v)"
              />
              <NumericInput
                type="text"
                label="MQTT Port"
                :value="fields.mqttPort"
                placeholder="e.g. 1883"
                @update:value="(v: string) => (fields.mqttPort = v)"
              />
            </div>
            <p v-if="fieldsError" class="error_message mb-2">{{ fieldsError }}</p>
            <div class="form-actions">
              <button
                class="generate-btn"
                type="button"
                :disabled="!isFieldsDirty || fieldsMutation.isPending.value"
                @click="saveFields"
              >
                <Loader v-if="fieldsMutation.isPending.value" />
                <span v-else>Save fields</span>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useApi } from '@/config/apiFetcher'
import useOnboardingDevices from '@/composables/Device/useOnboardingDevices'
import { appStore } from '@/store/app'
import { useUserStore } from '@/store/userStore'
import { uuid } from '@/helpers'
import log from '@/utils/useLogger'
import Icon from '@/components/Icon.vue'
import Loader from '@/components/ui/Loader.vue'
import NumericInput from '@/components/Inputs/NumericInput.vue'
import type { OnboardingDevice } from '@/types/onboarding.types'

const api = useApi()
const baseUrl = import.meta.env.VITE_APP_HTTP_ENPOINT
const { addNotifications } = appStore()
const { useUpdateDeviceMutation, useOrgDevicesQuery } = useOnboardingDevices()
const fieldsMutation = useUpdateDeviceMutation()
const { organisationId } = storeToRefs(useUserStore())
const orgDevicesQuery = useOrgDevicesQuery(organisationId)

type LookupKind = 'idi_device_id' | 'serial_number' | 'deviceID'

const lookupKind = ref<LookupKind>('idi_device_id')
const lookupValue = ref('')
const lookupLoading = ref(false)
const lookupError = ref('')

const lookupLabel = computed(() => {
  if (lookupKind.value === 'idi_device_id') return 'IDI device ID'
  if (lookupKind.value === 'serial_number') return 'Serial number'
  return 'deviceID (your org)'
})
const lookupPlaceholder = computed(() => {
  if (lookupKind.value === 'idi_device_id') return 'idids-29721'
  if (lookupKind.value === 'serial_number') return 'DJI-DOCK3-00041'
  return 'e.g. 86021229'
})

const matchedDevice = ref<OnboardingDevice | null>(null)

const fields = reactive({
  machineName: '',
  deviceIp: '',
  mqttHost: '',
  mqttPort: '',
})
const fieldsError = ref('')

watch(
  matchedDevice,
  (d) => {
    fields.machineName = d?.machine_name || ''
    fields.deviceIp = d?.device_ip || ''
    fields.mqttHost = d?.mqtt_host || ''
    fields.mqttPort = d?.mqtt_port != null ? String(d.mqtt_port) : ''
    fieldsError.value = ''
  },
  { immediate: true },
)

const isFieldsDirty = computed(() => {
  const d = matchedDevice.value
  if (!d) return false
  return (
    fields.machineName !== (d.machine_name || '') ||
    fields.deviceIp !== (d.device_ip || '') ||
    fields.mqttHost !== (d.mqtt_host || '') ||
    fields.mqttPort !== (d.mqtt_port != null ? String(d.mqtt_port) : '')
  )
})

const lookup = async () => {
  const value = lookupValue.value.trim()
  if (!value) return
  lookupLoading.value = true
  lookupError.value = ''
  matchedDevice.value = null
  try {
    if (lookupKind.value === 'deviceID') {
      if (orgDevicesQuery.isPending.value) {
        await orgDevicesQuery.refetch()
      }
      const list = orgDevicesQuery.data.value ?? []
      const device = list.find((d) => d.deviceID === value) || null
      if (!device) {
        lookupError.value = 'No device with that deviceID in your current org'
        return
      }
      matchedDevice.value = device
      return
    }
    const url = `${baseUrl}device-management/devices?${lookupKind.value}=${encodeURIComponent(value)}`
    const res = await api.get(url)
    const device = (res.data?.device ?? null) as OnboardingDevice | null
    if (!device) {
      lookupError.value = 'No device found'
      return
    }
    matchedDevice.value = device
  } catch (err: any) {
    const status = err?.response?.status
    if (status === 404) {
      lookupError.value = 'Device not found in any org you can access'
    } else if (status === 409) {
      lookupError.value =
        'This device is registered to another org. You must be authenticated as that org to update it.'
    } else {
      lookupError.value = err?.response?.data?.error || err?.message || 'Lookup failed'
    }
    log.error('admin device lookup failed', err)
  } finally {
    lookupLoading.value = false
  }
}

const saveFields = async () => {
  const d = matchedDevice.value
  if (!d) return
  if (
    !fields.machineName.trim() ||
    !fields.deviceIp.trim() ||
    !fields.mqttHost.trim() ||
    !fields.mqttPort.trim()
  ) {
    fieldsError.value = 'All four fields are required.'
    return
  }
  const port = Number(fields.mqttPort)
  if (!Number.isFinite(port) || port <= 0) {
    fieldsError.value = 'MQTT Port must be a positive number.'
    return
  }
  fieldsError.value = ''
  try {
    const updated = await fieldsMutation.mutateAsync({
      organisationID: d.organisationID,
      deviceID: d.deviceID,
      machine_name: fields.machineName.trim(),
      tailscale_id: fields.machineName.trim(),
      device_ip: fields.deviceIp.trim(),
      mqtt_host: fields.mqttHost.trim(),
      mqtt_port: port,
    })
    matchedDevice.value = updated
    addNotifications({
      id: uuid(10),
      notification: 'Device fields updated',
      type: 'success',
    })
  } catch (err: any) {
    fieldsError.value = err?.response?.data?.error || err?.message || 'Failed to update device fields'
    log.error('admin device fields update failed', err)
  }
}

const reset = () => {
  lookupValue.value = ''
  lookupError.value = ''
  matchedDevice.value = null
}
</script>

<style scoped>
.fields-route {
  padding-bottom: 2rem;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
@media (max-width: 1200px) {
  .grid-layout {
    grid-template-columns: 1fr;
  }
}

.form-card {
  background: linear-gradient(145deg, var(--idi-shade0-100) 0%, rgba(11, 14, 18, 0.95) 100%);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(27, 146, 166, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--txt-primary);
  margin: 0;
}
.card-subtitle {
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
  margin: 2px 0 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lookup-toggle {
  display: flex;
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-lg);
  padding: 4px;
  gap: 4px;
}
.lookup-toggle-btn {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--txt-secondary-1);
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: var(--rad-m);
  cursor: pointer;
  font-family: var(--primary-font);
  transition: all 0.15s;
}
.lookup-toggle-btn:hover {
  color: var(--txt-primary);
}
.lookup-toggle-btn[data-active] {
  background: var(--idi-shade3-50);
  color: var(--txt-primary);
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
.generate-btn {
  flex: 1;
  background: #1b92a6;
  color: #fff;
  border: none;
  border-radius: var(--rad-m);
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--primary-font);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}
.generate-btn:hover:not(:disabled) {
  opacity: 0.9;
}
.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.clear-btn {
  background: transparent;
  border: 1px solid var(--idi-shade3-100);
  color: var(--txt-secondary-1);
  border-radius: var(--rad-m);
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--primary-font);
  cursor: pointer;
}
.clear-btn:hover:not(:disabled) {
  background: var(--idi-shade3-50);
  color: var(--txt-primary);
}
.clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-match {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  border: 1px dashed var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  color: var(--txt-secondary-1);
  font-size: 0.8125rem;
  text-align: center;
}

.match-panel {
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-lg);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.match-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;
  align-items: center;
}
.match-label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--txt-secondary-2);
  font-weight: 600;
}
.match-value {
  font-size: 0.8125rem;
  color: var(--txt-primary);
}
.match-value.mono {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
}

.fields-section {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.error_message {
  color: #ef4444;
  font-size: 0.8125rem;
}
</style>
